// Read-only tenant detail page for landlords. Reached by tapping a
// tenant name on PropsListPage (or the new Details link on TenantsPage).
// Surfaces the same data the edit form captures — name / phone /
// email / emergency contact / assigned property / rent due day /
// ID verification / agreement details — but without making everything
// an input. An "Edit" button at the top routes to the existing
// TenantFormPage (which reads localStorage.editTid the same way).
//
// We deliberately don't reuse TenantFormPage with a `readOnly` prop —
// the edit form has a lot of inline lookup / phone-association
// machinery that doesn't belong in a read-only viewer, and the
// presentation is genuinely different (compact rows vs label+input).

import React, { useContext, useState, useEffect } from "react";
import Ctx from "../../contexts/AppContext";
import { api } from "../../api";
import { crd, sp, rad } from "../../constants/styles";
import Ic from "../common/Ic";
import Avatar from "../common/Avatar";
import { formatPropAddr } from "../../utils/propertyAddr";
import { TenantReviewsModal } from "../common/Reviews";

const ID_TYPE_LABELS = {
  aadhaar: "Aadhaar Card",
  pan: "PAN Card",
  driving_license: "Driving License",
  passport: "Passport",
  voter_id: "Voter ID",
};

const RENT_DUE_LABELS = {
  1: "1st of every month", 5: "5th of every month", 10: "10th of every month",
  15: "15th of every month", 20: "20th of every month", 25: "25th of every month",
};

// Strip everything except digits and a leading +, so a phone shown as
// "97900 62249" still dials correctly.
const telHref = (v) => "tel:" + String(v || "").replace(/[^\d+]/g, "");

function Row({ label, value, h, tel }) {
  // Compact key/value row with subtle bottom divider. Falls back to
  // em-dash when value is empty so the layout still reads as
  // "this field exists but isn't filled in yet" — easier for the
  // landlord to spot gaps than rendering an invisible empty row.
  //
  // When `tel` is set and a value exists, the value renders as a
  // click-to-call link (accent color + 📞) that opens the dialer on
  // mobile / the default handler on desktop.
  const hasVal = value !== undefined && value !== null && String(value).trim() !== "";
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "flex-start",
      padding: "10px 0",
      borderBottom: `1px solid ${h.bd}40`,
      gap: 12,
    }}>
      <span style={{ color: h.sc, fontSize: 12, minWidth: 110, flexShrink: 0 }}>{label}</span>
      {hasVal && tel ? (
        <a
          href={telHref(value)}
          style={{
            color: h.ac, fontSize: 13, fontWeight: 600, textAlign: "right",
            wordBreak: "break-word", textDecoration: "none",
            display: "inline-flex", alignItems: "center", gap: 5,
          }}
        >
          <span style={{ fontSize: 11 }}>{"📞"}</span>{value}
        </a>
      ) : (
        <span style={{ color: h.tx, fontSize: 13, fontWeight: 500, textAlign: "right", wordBreak: "break-word" }}>
          {hasVal ? value : <span style={{ color: h.tt }}>—</span>}
        </span>
      )}
    </div>
  );
}

function SectionHeader({ icon, title, h }) {
  return (
    <div style={{ color: h.ac, fontWeight: 700, fontSize: 12, marginBottom: 6, marginTop: 6, display: "flex", alignItems: "center", gap: 6 }}>
      <span>{icon}</span>
      <span style={{ textTransform: "uppercase", letterSpacing: 0.4 }}>{title}</span>
    </div>
  );
}

export default function TenantDetailPage() {
  const { t, h, tens, setTens, props, setProps, leases, go, setSelTid, flash, showConfirm, plan } = useContext(Ctx);
  const hasWa = (plan?.features || []).includes("whatsapp");
  const tid = localStorage.getItem("editTid");
  const te = tens.find(x => x.id === tid);
  const [showReviews, setShowReviews] = useState(false);
  // This tenant's reputation (avg + count), shown as a tappable chip next to
  // the name. Tapping opens the full reviews + a Rate button.
  const [tenantRep, setTenantRep] = useState(null);
  const loadRep = () => { if (te?.ph) api.getTenantReputation(te.ph).then(d => { if (d && d.success) setTenantRep(d); }).catch(() => {}); };
  useEffect(() => {
    if (!te?.ph) return;
    let alive = true;
    api.getTenantReputation(te.ph).then(d => { if (alive && d && d.success) setTenantRep(d); }).catch(() => {});
    return () => { alive = false; };
  }, [te?.ph]);

  // Lifted out of TenantsPage so the destructive + outbound-comm
  // actions all live on the canonical detail page (TenantsPage card
  // is now scan-only, with just Edit kept inline for quick access).
  const handleDelete = () => {
    if (!te) return;
    showConfirm(t.deleteTenConfirm, async () => {
      const res = await api.deleteTenant(te.id);
      // The server refuses to hard-delete a tenant who has a lease or
      // payment history (it would orphan their records and erase the
      // property from their move-out history). Surface that reason and
      // leave local state untouched — nothing was deleted.
      if (!res || !res.success) {
        flash(res?.error || t.tenDeleteFailed || "Couldn't delete this tenant.");
        return;
      }
      setTens(tens.filter(x => x.id !== te.id));
      setProps(props.map(p => p.tid === te.id ? { ...p, occ: false, tid: null } : p));
      flash(t.tenDeleted);
      go("tens"); // bounce back to the list since this record is gone
    });
  };

  const handleWhatsAppInvite = () => {
    if (!te?.ph) return;
    const phNorm  = te.ph.replace(/\D/g, "");
    const finalPh = phNorm.length === 10 ? "91" + phNorm : phNorm;
    // Security: never embed password in WhatsApp invite (CVE-style risk).
    const msg = `Hello ${te.name},%0AWelcome to ProRentz! 🏠%0AHere are your login details:%0ALink: https://lacto.in%0ARole: Tenant%0APhone Number: ${te.ph}%0APlease contact your landlord for your login password.`;
    window.open(`https://wa.me/${finalPh}?text=${msg}`, "_blank");
  };

  if (!te) {
    return (
      <div style={{ paddingTop: 16 }}>
        <button onClick={() => go("tens")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 8 }}>← {t.back || "Back"}</button>
        <div style={{ ...crd(h), textAlign: "center", padding: 32 }}>
          <Ic n="user" s={32} c={h.sc} />
          <div style={{ color: h.tx, fontSize: 14, fontWeight: 600, marginTop: 12 }}>{t.tenantNotFound || "Tenant not found"}</div>
          <div style={{ color: h.sc, fontSize: 12, marginTop: 6 }}>The tenant may have been deleted. Returning to the list will refresh.</div>
          <button onClick={() => go("tens")} style={{
            marginTop: 16, padding: "8px 20px", borderRadius: 8, border: "none",
            background: h.ac, color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600,
          }}>← {t.back || "Back to tenants"}</button>
        </div>
      </div>
    );
  }

  const myProp = te.pid ? props.find(p => p.id === te.pid) : null;
  const myLease = leases?.find(l => l.tenant_id === te.id && l.status === 'active') || leases?.find(l => l.tenant_id === te.id);
  const idTypeLabel = ID_TYPE_LABELS[te.id_type] || te.id_type || "";
  const rentDueLabel = RENT_DUE_LABELS[te.rent_due_day] || (te.rent_due_day ? `${te.rent_due_day} of every month` : "");
  const aad = te.aadhaar_full || te.aadhaar || (te.aad ? `XXXX XXXX ${te.aad}` : "");
  const idLast4 = te.aad || (te.id_number ? String(te.id_number).slice(-4) : "");

  return (
    <div style={{ paddingTop: 16 }}>
      {/* Header row — back + Edit (which routes to the existing edit form) */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <button onClick={() => go("tens")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", gap: 4 }}>
          <Ic n="arrow-left" s={14} c={h.ac} /> {t.back || "Back"}
        </button>
        <button onClick={() => {
          // Keep the same localStorage key — TenantFormPage reads
          // editTid to know which tenant to load.
          localStorage.setItem("editTid", te.id);
          go("editTenant");
        }} style={{
          background: `${h.ac}15`, border: `1px solid ${h.ac}`,
          color: h.ac, padding: "5px 14px", borderRadius: 8,
          cursor: "pointer", fontSize: 12, fontWeight: 600,
          display: "flex", alignItems: "center", gap: 4,
        }}>
          <Ic n="edit" s={12} c={h.ac} /> {t.edit || "Edit"}
        </button>
      </div>

      {/* Tenant header card — avatar + name + status */}
      <div style={{ ...crd(h), padding: 16, marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <Avatar src={te.avatar_url} name={te.name} size={56} bg={h.ac} fontSize={20} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: h.tx, fontWeight: 700, fontSize: 18, marginBottom: 2 }}>{te.name || "—"}</div>
            <div style={{ color: h.sc, fontSize: 12 }}>
              {te.ph || "—"}
              {te.join_date && <span> · Since {te.join_date}</span>}
            </div>
            <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
              {/* Tapping the badge jumps to the Mark-as-Verified toggle
                  in the edit form (focusVerify flag → scroll + highlight). */}
              {(() => {
                const goVerify = () => {
                  localStorage.setItem("editTid", te.id);
                  localStorage.setItem("focusVerify", "1");
                  go("editTenant");
                };
                return te.verified ? (
                  <span onClick={goVerify} title="Manage verification"
                    style={{ background: `${h.gn}15`, color: h.gn, padding: "2px 10px", borderRadius: 999, fontSize: 10, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 3 }}>
                    ✓ Verified <span style={{ opacity: 0.7 }}>{"›"}</span>
                  </span>
                ) : (
                  <span onClick={goVerify} title="Mark as verified"
                    style={{ background: `${h.wn}15`, color: h.wn, padding: "2px 10px", borderRadius: 999, fontSize: 10, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 3 }}>
                    Not verified <span style={{ opacity: 0.7 }}>{"›"}</span>
                  </span>
                );
              })()}
              {te.detached_at && (
                <span style={{ background: `${h.tt || h.sc}15`, color: h.sc, padding: "2px 10px", borderRadius: 999, fontSize: 10, fontWeight: 700 }}>
                  Detached
                </span>
              )}
              {/* Reputation chip — tap to read this tenant's reviews + rate them */}
              <span onClick={() => setShowReviews(true)} title="View / write reviews"
                style={{ background: `${h.ac}12`, color: h.ac, padding: "2px 10px", borderRadius: 999, fontSize: 10, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
                <span style={{ color: "#f59e0b" }}>★</span>
                {tenantRep && tenantRep.count > 0 ? `${Number(tenantRep.avg).toFixed(1)} (${tenantRep.count})` : "Reviews"}
                <span style={{ opacity: 0.7 }}>{"›"}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {showReviews && <TenantReviewsModal phone={te.ph} name={te.name} h={h} t={t} flash={flash}
        onClose={() => setShowReviews(false)} onRated={loadRep} />}

      {/* Contact info */}
      <div style={{ ...crd(h), padding: 16, marginBottom: 12 }}>
        <SectionHeader icon="👤" title="Contact" h={h} />
        <Row label="Phone" value={te.ph} h={h} tel />
        <Row label="Email" value={te.email} h={h} />
        <Row label="Emergency Contact" value={te.emergency_contact} h={h} tel />
      </div>

      {/* Property + rent */}
      <div style={{ ...crd(h), padding: 16, marginBottom: 12 }}>
        <SectionHeader icon="🏠" title="Property & Rent" h={h} />
        <Row label="Property" value={myProp ? formatPropAddr(myProp) : ""} h={h} />
        <Row label="Rent Due Day" value={rentDueLabel} h={h} />
        <Row label="Join Date" value={te.join_date} h={h} />
        {myProp && <Row label="Rent" value={myProp.rent ? `₹${Number(myProp.rent).toLocaleString()}/mo` : ""} h={h} />}
        {myProp && <Row label="Deposit" value={myProp.dep ? `₹${Number(myProp.dep).toLocaleString()}` : ""} h={h} />}
        {myLease && <Row label="Lease Status" value={myLease.status || ""} h={h} />}
      </div>

      {/* ID Verification */}
      <div style={{ ...crd(h), padding: 16, marginBottom: 12 }}>
        <SectionHeader icon="🛡️" title="ID Verification" h={h} />
        <Row label="ID Type" value={idTypeLabel} h={h} />
        <Row label="ID Number" value={aad} h={h} />
        {idLast4 && !aad && <Row label="ID Last 4" value={idLast4} h={h} />}
        {te.id_proof_url ? (
          <div style={{ padding: "10px 0", borderBottom: `1px solid ${h.bd}40` }}>
            <div style={{ color: h.sc, fontSize: 12, marginBottom: 6 }}>ID Proof Document</div>
            <a href={te.id_proof_url} target="_blank" rel="noopener noreferrer" style={{ display: "inline-block" }}>
              <img src={te.id_proof_url} alt="ID proof" style={{
                maxWidth: 200, maxHeight: 120, borderRadius: rad.sm, border: `1px solid ${h.bd}`,
              }} />
            </a>
          </div>
        ) : (
          <Row label="ID Proof Document" value="" h={h} />
        )}
      </div>

      {/* Agreement details (only show when at least one is filled) */}
      {(te.parent_name || te.relation || te.address) && (
        <div style={{ ...crd(h), padding: 16, marginBottom: 12 }}>
          <SectionHeader icon="📋" title="Agreement Details" h={h} />
          <Row label="Parent / Spouse" value={te.parent_name} h={h} />
          <Row label="Relation" value={te.relation} h={h} />
          <Row label="Permanent Address" value={te.address} h={h} />
        </div>
      )}

      {/* E-signature preview if present */}
      {te.esign && (
        <div style={{ ...crd(h), padding: 16, marginBottom: 12 }}>
          <SectionHeader icon="✍️" title="E-Signature on File" h={h} />
          {te.esign.startsWith("sig|") || te.esign.startsWith("data:image/") ? (
            <img
              src={te.esign.startsWith("sig|") ? te.esign.split("|").slice(2).join("|") : te.esign}
              alt="signature"
              style={{ maxHeight: 60, background: "#fff", padding: 4, borderRadius: 6, border: `1px solid ${h.bd}`, display: "block" }}
            />
          ) : (
            <div style={{ color: h.tx, fontSize: 12 }}>{te.esign}</div>
          )}
          {te.esign.startsWith("sig|") && (
            <div style={{ color: h.sc, fontSize: 10, marginTop: 4 }}>Signed on {te.esign.split("|")[1]}</div>
          )}
        </div>
      )}

      {/* ───── Action group ────────────────────────────────────────
          All tenant actions live here on the detail page (previously
          split between the TenantsPage card + this page). One canonical
          place to act on a tenant; the list card stays scan-focused.

          Hierarchy: primary edit → secondary view → conditional
          invite → separated destructive delete.
          ────────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>

        {/* Primary: Edit Tenant */}
        <button onClick={() => {
          localStorage.setItem("editTid", te.id);
          go("editTenant");
        }} style={{
          width: "100%", padding: "12px 16px",
          borderRadius: rad.md, border: "none",
          background: h.ac, color: "#fff",
          fontSize: 14, fontWeight: 700, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
        }}>
          <Ic n="edit" s={14} c="#fff" /> {t.editTenant || "Edit Tenant"}
        </button>

        {/* Secondary: View Payment History */}
        <button onClick={() => { setSelTid(te.id); go("tenantPayHistory"); }}
          style={{
            width: "100%", padding: "11px 16px",
            borderRadius: rad.md,
            border: `1px solid ${h.ac}`,
            background: `${h.ac}10`, color: h.ac,
            fontSize: 13, fontWeight: 600, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          }}>
          {"🧾 "} {t.viewPayHistory || "View Payment History"}
        </button>

        {/* Rate / view reviews — opens the consolidated reviews modal (past
            reviews + a Rate button). Server validates eligibility (their
            tenant + tenancy >= 30 days or moved out). */}
        <button onClick={() => setShowReviews(true)}
          style={{
            width: "100%", padding: "11px 16px",
            borderRadius: rad.md,
            border: `1px solid ${h.gn || "#16a34a"}`,
            background: `${h.gn || "#16a34a"}10`, color: h.gn || "#16a34a",
            fontSize: 13, fontWeight: 600, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          }}>
          {"★ "} {t.rateTenant || "Rate Tenant"}
        </button>

        {/* Tertiary: Invite via WhatsApp (only if plan allows + phone set) */}
        {hasWa && te.ph && (
          <button onClick={handleWhatsAppInvite}
            style={{
              width: "100%", padding: "11px 16px",
              borderRadius: rad.md, border: "none",
              background: "#25D366", color: "#fff",
              fontSize: 13, fontWeight: 600, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
            {"💬 "} Invite via WhatsApp
          </button>
        )}

        {/* Destructive: Delete — visually separated by extra top margin
            and confined width so it doesn't read as a normal action. */}
        <button onClick={handleDelete}
          style={{
            width: "100%", padding: "11px 16px",
            marginTop: 6,
            borderRadius: rad.md,
            border: `1px solid ${h.rd}40`,
            background: `${h.rd}10`, color: h.rd,
            fontSize: 13, fontWeight: 600, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          }}>
          {"✕ "} {t.delete || "Delete"} Tenant
        </button>
      </div>
    </div>
  );
}
